import {Props as ItemProps} from './Item';

export interface Item<
  ItemType extends Item<ItemType>,
  StaticPropsType extends object = object,
  TSectionProps extends {id: string} = {id: string},
> {
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  callback?: () => void;
  children?: React.ReactNode;
  id?: string;
  inactive?: boolean;
  inactiveStyle?: boolean;
  /**
   * Prevents the selection of this item by avoiding selection in the table or low-level list component
   */
  notSelectable?: boolean;
  ItemComponent?: React.FunctionComponent<
    ItemProps<ItemType> & StaticPropsType & {staticProps?: StaticPropsType}
  >;
  section?: string | number | TSectionProps;
  flipSubMenuPopupPosition?: boolean;
  title?: string;
  url?: string;
}

export enum ActionType {
  SetItems = 'SET_ITEMS',
  SetActiveItem = 'SET_ACTIVE_ITEM',
  SetSelection = 'SET_SELECTION',
  AppendToSelection = 'APPEND_TO_SELECTION',
  SelectDefault = 'SELECT_DEFAULT',
  ToggleSelection = 'TOGGLE_SELECTION',
  Navigate = 'NAVIGATE',
  SetMultiselecting = 'SET_MULTISELECTING',
}

export enum Navigation {
  Down,
  Up,
  Home,
  End,
  Item,
}

export enum NavigationMode {
  Normal,
  // Select everything between current active item and the target inclusive
  Selecting,
}

export interface SetItems<TItemType extends Item<TItemType> = Item<object>> {
  type: ActionType.SetItems;
  payload: {
    newItems: TItemType[];
  };
}

export interface SetActiveItem<
  TItemType extends Item<TItemType> = Item<object>,
> {
  type: ActionType.SetActiveItem;
  payload: {
    newActiveItem?: TItemType;
  };
}

export interface SetSelection<
  TItemType extends Item<TItemType> = Item<object>,
> {
  type: ActionType.SetSelection;
  payload: {
    newActiveItem?: TItemType;
    newSelection: TItemType[];
  };
}

export interface AppendToSelection<
  TItemType extends Item<TItemType> = Item<object>,
> {
  type: ActionType.AppendToSelection;
  payload: {
    appendingSelection: TItemType[];
  };
}

export interface SelectDefault {
  type: ActionType.SelectDefault;
}

export interface ToggleSelection<
  TItemType extends Item<TItemType> = Item<object>,
> {
  type: ActionType.ToggleSelection;
  payload: {
    togglingItem: TItemType;
  };
}

export interface Navigate<TItemType extends Item<TItemType> = Item<object>> {
  type: ActionType.Navigate;
  payload:
    | {
        navigation:
          | Navigation.Up
          | Navigation.Down
          | Navigation.Home
          | Navigation.End;
        mode: NavigationMode;
      }
    | {
        navigation: Navigation.Item;
        mode: NavigationMode;
        item: TItemType;
      };
}

export interface SetMultiselecting {
  type: ActionType.SetMultiselecting;
  payload: {
    multiselecting: boolean;
  };
}

export type Action<TItemType extends Item<TItemType> = Item<object>> =
  | SetItems<TItemType>
  | SetActiveItem<TItemType>
  | AppendToSelection<TItemType>
  | SetSelection<TItemType>
  | SelectDefault
  | ToggleSelection<TItemType>
  | Navigate<TItemType>
  | SetMultiselecting;

export function setItems<TItemType extends Item<TItemType> = Item<object>>(
  newItems: TItemType[]
): SetItems<TItemType> {
  return {
    payload: {newItems},
    type: ActionType.SetItems,
  };
}

export function setActiveItem<TItemType extends Item<TItemType> = Item<object>>(
  newActiveItem?: TItemType
): SetActiveItem<TItemType> {
  return {
    payload: {newActiveItem},
    type: ActionType.SetActiveItem,
  };
}

export function appendToSelection<
  TItemType extends Item<TItemType> = Item<object>,
>(appendingSelection: TItemType[]): AppendToSelection<TItemType> {
  return {
    payload: {appendingSelection},
    type: ActionType.AppendToSelection,
  };
}

export function setSelection<TItemType extends Item<TItemType> = Item<object>>({
  newActiveItem,
  newSelection,
}: {
  newActiveItem?: TItemType;
  newSelection: TItemType[];
}): SetSelection<TItemType> {
  return {
    payload: {newActiveItem, newSelection},
    type: ActionType.SetSelection,
  };
}

export function toggleSelection<
  TItemType extends Item<TItemType> = Item<object>,
>(togglingItem: TItemType): ToggleSelection<TItemType> {
  return {
    payload: {togglingItem},
    type: ActionType.ToggleSelection,
  };
}

export function navigate<TItemType extends Item<TItemType> = Item<object>>(
  navigation:
    | Navigation.Up
    | Navigation.Down
    | Navigation.Home
    | Navigation.End
    | TItemType,
  mode: NavigationMode
): Navigate<TItemType> {
  if (
    navigation === Navigation.Up ||
    navigation === Navigation.Down ||
    navigation === Navigation.Home ||
    navigation === Navigation.End
  ) {
    return {
      payload: {mode, navigation},
      type: ActionType.Navigate,
    };
  } else {
    return {
      payload: {item: navigation, mode, navigation: Navigation.Item},
      type: ActionType.Navigate,
    };
  }
}

export function selectDefault(): SelectDefault {
  return {type: ActionType.SelectDefault};
}

export function setMultiselecting(multiselecting: boolean): SetMultiselecting {
  return {
    payload: {multiselecting},
    type: ActionType.SetMultiselecting,
  };
}
